import { Menu, MessageCircle, Phone, X } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { company } from '../data/company';
import { categories } from '../data/products';
import { LanguageSwitcher } from './LanguageSwitcher';
import { localizePath } from '../i18n/config';

export function Layout({ children, lang }: { children: ReactNode; lang: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const localized = (path: string) => localizePath(path, lang);

  const navigation = [
    { label: t('nav.home'), path: '/' },
    { label: t('nav.products'), path: '/products' },
    { label: t('nav.blog'), path: '/blog' },
    { label: t('nav.about'), path: '/company' },
    { label: t('nav.contact'), path: '/contact' }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to={localized('/')} className="flex items-center gap-2" aria-label="GyroNavi home">
            <span className="text-2xl font-black">
              Gyro<span className="text-aqua">Navi</span>
            </span>
          </Link>
          <nav className="hidden items-center gap-6 lg:flex">
            {navigation.map((item) => (
              <NavLink
                key={item.path}
                to={localized(item.path)}
                className={({ isActive }) =>
                  `text-sm font-semibold transition hover:text-aqua ${isActive ? 'text-aqua' : 'text-slate-700'}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <LanguageSwitcher />
            <a className="btn-secondary px-4 py-2" href={`tel:${company.phone}`}>
              <Phone size={17} /> {t('actions.call')}
            </a>
            <Link className="btn-primary px-4 py-2" to={localized('/contact')}>
              <MessageCircle size={17} /> {t('actions.requestQuote')}
            </Link>
          </div>
          <button
            className="grid h-11 w-11 place-items-center rounded-md border border-slate-300 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label={t('actions.openMenu')}
          >
            <Menu />
          </button>
        </div>
      </header>

      {open ? (
        <div className="fixed inset-0 z-50 bg-ink/40 lg:hidden" onClick={() => setOpen(false)}>
          <aside
            className="ms-auto h-full w-80 max-w-[88vw] bg-white p-6 shadow-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="mb-6 grid h-11 w-11 place-items-center rounded-md border" onClick={() => setOpen(false)}>
              <X />
            </button>
            <nav className="grid gap-4">
              {navigation.map((item) => (
                <NavLink key={item.path} to={localized(item.path)} className="font-semibold" onClick={() => setOpen(false)}>
                  {item.label}
                </NavLink>
              ))}
              <Link className="btn-primary mt-3" to={localized('/contact')} onClick={() => setOpen(false)}>
                {t('actions.requestQuote')}
              </Link>
              <div className="mt-3 border-t border-slate-200 pt-3">
                <LanguageSwitcher />
              </div>
            </nav>
          </aside>
        </div>
      ) : null}

      <main>{children}</main>

      <footer className="border-t border-slate-200 bg-ink text-white">
        <div className="section grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <p className="text-2xl font-black">
              {company.brand}
            </p>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300">
              {t('footer.description')}
            </p>
          </div>
          <div>
            <p className="font-bold">{t('nav.products')}</p>
            <div className="mt-3 grid gap-2">
              {categories.map((cat) => (
                <Link key={cat.slug} className="text-sm text-slate-300 hover:text-white" to={localized(`/products/${cat.slug}`)}>
                  {t(`products:categories.${cat.slug}.title`)}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="font-bold">{t('footer.quickLinks')}</p>
            <div className="mt-3 grid gap-2">
              <Link className="text-sm text-slate-300 hover:text-white" to={localized('/company')}>
                {t('nav.about')}
              </Link>
              <Link className="text-sm text-slate-300 hover:text-white" to={localized('/applications')}>
                {t('nav.applications')}
              </Link>
              <Link className="text-sm text-slate-300 hover:text-white" to={localized('/blog')}>
                {t('nav.blog')}
              </Link>
              <Link className="text-sm text-slate-300 hover:text-white" to={localized('/contact')}>
                {t('nav.contact')}
              </Link>
            </div>
          </div>
          <div>
            <p className="font-bold">{t('nav.contact')}</p>
            <div className="mt-3 grid gap-2 text-sm text-slate-300">
              <p>{t('footer.phone')}: <a className="hover:text-white" href={`tel:${company.phone}`}>{company.phoneDisplay}</a></p>
              <p>{t('footer.whatsapp')}: <a className="hover:text-white" href={company.whatsappUrl} target="_blank" rel="noopener noreferrer">{company.whatsapp}</a></p>
              <p>{t('footer.email')}: {company.email}</p>
              <p>{company.location}</p>
              <p>{company.hours}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-slate-400">
          &copy; 2026 {company.name} All rights reserved.
        </div>
      </footer>
    </div>
  );
}
